import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { 
  LocationConceptService, 
  LocationConceptAdminDto, 
  ConceptStatusDto,
  CreateLocationConceptRequest,
  UpdateLocationConceptRequest,
  StaffAssignmentDto,
  ConceptStaffDto
} from '../../../services/location-concept.service';
import { MasterModel } from '../../../../../master/models/master.model';

interface DialogData {
  concept: LocationConceptAdminDto | null;
  locationId: string;
  availableConcepts: MasterModel[];
  availableStaff: MasterModel[];
  conceptStatuses: ConceptStatusDto[];
}

@Component({
    selector: 'app-location-service-dialog',
    templateUrl: './location-service-dialog.component.html',
    styleUrls: ['./location-service-dialog.component.css'],
    standalone: false
})
export class LocationServiceDialogComponent implements OnInit {
  serviceForm: UntypedFormGroup;
  isLoading = false;
  errorMessage: string | null = null;
  isEditMode = false;

  constructor(
    private fb: UntypedFormBuilder,
    private conceptService: LocationConceptService,
    public dialogRef: MatDialogRef<LocationServiceDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData
  ) {
    this.isEditMode = !!this.data.concept;
  }

  ngOnInit(): void {
    this.buildForm();
    
    if (this.isEditMode && this.data.concept) {
      this.loadAssignedStaff();
    }
  }

  private loadAssignedStaff(): void {
    if (!this.data.locationId || !this.data.concept) return;

    this.isLoading = true;
    this.conceptService.getLocationConceptDetail(
      this.data.locationId,
      this.data.concept.merchantLocationConceptId
    ).subscribe(
      response => {
        this.isLoading = false;
        if (response && response.isSuccess && response.value) {
          const staffEmployeeIds = response.value.assignedStaff.map(s => s.employeeId).filter(id => id);
          this.serviceForm.patchValue({
            staffUids: staffEmployeeIds
          });
        }
      },
      error => {
        this.isLoading = false;
        console.error('Error loading assigned staff:', error);
        this.errorMessage = 'Error al cargar el personal asignado';
      }
    );
  }

  private buildForm(): void {
    const concept = this.data.concept;
    
    this.serviceForm = this.fb.group({
      conceptId: [concept ? concept.conceptId : '', this.isEditMode ? [] : [Validators.required]],
      
      title: [concept ? concept.title : '', [Validators.required, Validators.maxLength(200)]],
      description: [concept ? concept.description : '', Validators.maxLength(1000)],
      durationMinutes: [concept ? concept.durationMinutes : 30, [Validators.required, Validators.min(1)]],
      price: [concept ? concept.price : 0, [Validators.required, Validators.min(0)]],
      bufferBeforeMinutes: [concept ? concept.bufferBeforeMinutes : 0, Validators.min(0)],
      bufferAfterMinutes: [concept ? concept.bufferAfterMinutes : 0, Validators.min(0)],
      statusMerchantConceptId: [concept ? concept.statusMerchantConceptId : 1, Validators.required],
      
      staffUids: [[]]
    });
  }

  onConceptSelected(selectedConceptIdOrObject: string | any): void {
    const selectedConceptId = typeof selectedConceptIdOrObject === 'string' 
      ? selectedConceptIdOrObject 
      : (selectedConceptIdOrObject && selectedConceptIdOrObject.id ? selectedConceptIdOrObject.id : null);
    
    if (!selectedConceptId) return;
    
    const selectedConcept = this.data.availableConcepts.find(c => c.id === selectedConceptId);
    
    if (selectedConcept && !this.isEditMode) {
      const metadata = selectedConcept.metadata || {};
      this.serviceForm.patchValue({
        title: selectedConcept.display,
        description: metadata.description || '',
        durationMinutes: metadata.defaultDuration || 30,
        price: metadata.price !== undefined && metadata.price !== null ? metadata.price : 0
      });
    }
  }

  onSave(): void {
    if (!this.serviceForm.valid) {
      this.markFormGroupTouched(this.serviceForm);
      return;
    }

    this.isLoading = true;
    this.errorMessage = null;

    const formValue = this.serviceForm.value;
    
    const conceptId = formValue.conceptId 
      ? (typeof formValue.conceptId === 'string' ? formValue.conceptId : formValue.conceptId.id)
      : null;
    
    const staffAssignments: StaffAssignmentDto[] = this.getSelectedStaffAssignments(formValue.staffUids);

    if (this.isEditMode && this.data.concept) {
      const updateRequest: UpdateLocationConceptRequest = {
        title: formValue.title,
        description: formValue.description || null,
        durationMinutes: formValue.durationMinutes,
        price: formValue.price,
        bufferBeforeMinutes: formValue.bufferBeforeMinutes,
        bufferAfterMinutes: formValue.bufferAfterMinutes,
        statusMerchantConceptId: formValue.statusMerchantConceptId,
        staffAssignments: staffAssignments
      };

      this.conceptService.updateLocationConcept(
        this.data.locationId,
        this.data.concept.merchantLocationConceptId,
        updateRequest
      ).subscribe(
        response => {
          this.isLoading = false;
          if (response.isSuccess) {
            this.dialogRef.close(true); 
          } else {
            this.errorMessage = this.getErrorMessage(response.error);
          }
        },
        error => {
          this.isLoading = false;
          this.errorMessage = 'Error al actualizar el concepto. Inténtalo de nuevo.';
          console.error('Error updating concept:', error);
        }
      );
    } else {
      const createRequest: CreateLocationConceptRequest = {
        conceptId: conceptId,
        title: formValue.title,
        description: formValue.description || null,
        durationMinutes: formValue.durationMinutes,
        price: formValue.price,
        bufferBeforeMinutes: formValue.bufferBeforeMinutes,
        bufferAfterMinutes: formValue.bufferAfterMinutes,
        statusMerchantConceptId: formValue.statusMerchantConceptId,
        staffAssignments: staffAssignments
      };

      this.conceptService.createLocationConcept(this.data.locationId, createRequest).subscribe(
        response => {
          this.isLoading = false;
          if (response.isSuccess) {
            this.dialogRef.close(true); 
          } else {
            this.errorMessage = this.getErrorMessage(response.error);
          }
        },
        error => {
          this.isLoading = false;
          this.errorMessage = 'Error al crear el concepto. Inténtalo de nuevo.';
          console.error('Error creating concept:', error);
        }
      );
    }
  }

  private getSelectedStaffAssignments(staffUids: string[]): StaffAssignmentDto[] {
    if (!staffUids || staffUids.length === 0) {
      return [];
    }

    return staffUids
      .map(uid => {
        const staff = this.data.availableStaff.find(s => s.id === uid);
        if (!staff) return null;

        const metadata = staff.metadata || {};
        const displayName = staff.display;
        const nameParts = displayName.split(' ');
        const firstName = metadata.firstName || nameParts[0] || displayName;
        const lastName = metadata.lastName || (nameParts.length > 1 ? nameParts.slice(1).join(' ') : '');

        return {
          employeeId: staff.id,
          firstName: firstName,
          lastName: lastName,
          displayName: displayName,
          email: metadata.email || null,
          phone: metadata.phone || null
        };
      })
      .filter(s => s !== null) as StaffAssignmentDto[];
  }

  private getErrorMessage(error: any): string {
    if (!error) return 'Error desconocido';
    
    if (error.messages && error.messages.length > 0) {
      return error.messages.join(', ');
    }
    
    return error.message || 'Error al procesar la solicitud';
  }

  clearError(): void {
    this.errorMessage = null;
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }

  private markFormGroupTouched(formGroup: UntypedFormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control.markAsTouched();
    });
  }
  public trackByIndex(i: number) { return i; }

}
